import React, { ReactNode, useState } from 'react';
import IconButton, { IconButtonProps } from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';


interface ActionOptions {
  disabled?: boolean;
  divider?: boolean;
}

export interface MenuActionsProps {
  noAutoClose?: boolean;
  actions: (
    | [string, ReactNode, () => void]
    | [string, ReactNode, () => void, ActionOptions]
    | null
  )[];
  keepMenu?: boolean;
  className?: string;
  stopPropagation?: boolean;
}

interface Props
  extends MenuActionsProps,
    Pick<IconButtonProps, 'size' | 'color'> {
  icon?: ReactNode;
  strippedButton?: boolean;
}

/**
 * An icon button that opens a menu with actions.
 *
 * Each action has the form:
 *   [key, label, clickHandler]
 *
 * Or:
 *   [key, label, clickHandler, { disabled?, divider? }]
 *
 * Divider rows are identified by a key starting with "divider".
 */
export const MoreActionsMenuButton = (props: Props) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  const {
    actions,
    icon,
    noAutoClose,
    strippedButton,
    keepMenu,
    className,
    stopPropagation,
    ...iconButtonProps
  } = props;

  const availableActions = actions.filter(Boolean) as [
    string,
    ReactNode,
    () => void,
    ActionOptions | undefined
  ][];

  if (!availableActions.length) {
    return null;
  }

  const openMenu = (event: React.MouseEvent<HTMLElement>) => {
    if (stopPropagation) {
      event.stopPropagation();
    }

    // currentTarget is the button itself.
    // target may instead be the nested SVG/path element.
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => {
    setMenuAnchor(null);
  };

  const trigger = (
    <IconButton
      size="small"
      className={className}
      {...iconButtonProps}
      onClick={openMenu}
    >
      {icon || <MoreVertIcon />}
    </IconButton>
  );

  return (
    <>
      {trigger}

      {(Boolean(menuAnchor) || keepMenu) && (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          keepMounted={keepMenu}
        >
          {availableActions.map(([key, label, action, options]) => {
            if (key && key.startsWith('divider')) {
              return <MenuItem key={key} divider />;
            }

            return (
              <MenuItem
                key={key}
                divider={options?.divider}
                disabled={options?.disabled}
                onClick={(event) => {
                  if (stopPropagation) {
                    event.stopPropagation();
                  }

                  action();

                  if (!noAutoClose) {
                    closeMenu();
                  }
                }}
              >
                {label}
              </MenuItem>
            );
          })}
        </Menu>
      )}
    </>
  );
};